import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import OverflowingText from '../../../components/foundations/OverflowingText';
import Tooltip from '../../../components/molecules/Tooltip';
import { Label, Toggle } from '../../../components/widgets';
import ContextMenu from '../../../components/widgets/ContextMenu';
import PermissionsWrapper from '../../../containers/PermissionsWrapper';
import { downloadFileResponse } from '../../../global/utils';
import SkanPlusWhiteIcon from '../../../resources/svg/skan_plus/skan-small-white.svg';
import SkanPlusIcon from '../../../resources/svg/skan_plus/skan-small.svg';
import { getReadOnly } from '../../../selectors/permissions';
import { OPTIMIZED_MODELS_EVENT_PREFIX, trackMixpanelEvent } from '../../../utils/general';
import toaster from '../../../utils/toaster';
import css from '../../components/ConversionModelHeader.css';
import { SUPPORTED_MODEL_KINDS } from '../../components/skan4/modelTypes/Factories/ModelTypeFactory';
import { conversionModelTypeShape, optimizedModelsShape } from '../../components/skan4/types';
import { ModelsApiContext } from '../../hooks';
import SKAdnetworkAPI from '../../service';
import { getModelKindName } from '../../utils';

const api = new SKAdnetworkAPI();

const ConversionModelsGroupHeader = ({
    name,
    groupId,
    model,
    editable = true,
    onToggleClick = () => {},
    onArchiveClick = () => {},
    relevantOptimizedModels = [],
    isSkan4 = false,
    isReadOnly = false,
    translate = () => {},
}) => {
    const { onModelGroupEditClick, handleUpgradeToSkan4Click, isAdminMode } = useContext(ModelsApiContext);

    const readOnly = useSelector(getReadOnly);

    const [optimizedModelImprovementPercentage, setOptimizedModelImprovementPercentage] = useState(0);
    const [isOptimizedModelButtonHovered, setIsOptimizedModelButtonHovered] = useState(false);

    const { enabled, typeDisplayName, modelType, revenueType, eventsType } = model;
    const isModelEditable = editable || isAdminMode;
    // Read-only custom override groups still need an enabled menu so the archive action is reachable.
    const isMenuEnabled =
        isReadOnly || SUPPORTED_MODEL_KINDS.includes(getModelKindName(modelType, revenueType, eventsType));

    useEffect(() => {
        if (relevantOptimizedModels.length) {
            setOptimizedModelImprovementPercentage(
                Math.max(
                    ...relevantOptimizedModels.map(({ optimized_model_improvement: modelImprovement }) =>
                        Math.floor(modelImprovement * 100)
                    )
                )
            );
        } else {
            setOptimizedModelImprovementPercentage(0);
        }
    }, [relevantOptimizedModels]);

    const onOptimizedModelAvailableClicked = () => {
        trackMixpanelEvent(OPTIMIZED_MODELS_EVENT_PREFIX, 'Optimized Model Available Clicked', {});
        onModelGroupEditClick(groupId, true);
    };

    const onPreviewClick = () => {
        api.previewGroupModel(groupId)
            .then(res => {
                downloadFileResponse(res, true);
            })
            .catch(() => {
                toaster.error(translate('STATIC.GENERAL_MESSAGES.UNEXPECTED_ERROR'));
            });
    };

    const getMenuItems = () => {
        const editOrView = readOnly
            ? {
                  name: 'STATIC.PAGES.SKADNETWORK.VIEW_BUTTON',
                  onClick: () => onModelGroupEditClick(groupId),
              }
            : {
                  name: 'STATIC.PAGES.SKADNETWORK.EDIT_BUTTON',
                  disabled: !isModelEditable,
                  tooltip: isModelEditable ? null : 'STATIC.PAGES.SKADNETWORK.CUSTOM_MODEL_EDIT_DISABLED_TOOLTIP',
                  onClick: () => onModelGroupEditClick(groupId),
              };

        const preview = {
            name: 'STATIC.PAGES.SKADNETWORK.PREVIEW_MODEL',
            onClick: onPreviewClick,
        };

        const archive = {
            name: 'STATIC.PAGES.SKADNETWORK.ARCHIVE_BUTTON',
            disabled: !isModelEditable || enabled,
            tooltip: isModelEditable ? null : 'STATIC.PAGES.SKADNETWORK.ENABLED_MODEL_ARCHIVE_DISABLED_TOOLTIP',
            onClick: () => onArchiveClick(groupId),
        };

        // Read-only custom override groups: no edit/view and no preview - only archive (turn-off via the toggle).
        if (isReadOnly) {
            return readOnly ? [] : [archive];
        }

        if (readOnly) return [editOrView, preview];

        const menuItems = [editOrView, preview, archive];

        if (!isSkan4) {
            menuItems.push({
                name: 'STATIC.PAGES.SKADNETWORK.SKAN_4_UPGRADE',
                onClick: () => handleUpgradeToSkan4Click(groupId),
                isNew: true,
            });
        }

        return menuItems;
    };

    const actionMenuOnSelect = (item, propsVal) => {
        item.onClick(propsVal);
    };

    return (
        <div className={css.container}>
            <Tooltip title={name}>
                <OverflowingText>
                    <Label className={css.modelName} text={name} type="subtitle" />
                </OverflowingText>
            </Tooltip>
            <div className={css.rightArea} onClick={e => e.stopPropagation()}>
                {optimizedModelImprovementPercentage > 0 && (
                    <PermissionsWrapper write>
                        <div
                            className={classNames(css.optimizedModelButton, {
                                [css.optimizedModelButtonHover]: isOptimizedModelButtonHovered,
                            })}
                            onClick={onOptimizedModelAvailableClicked}
                            onMouseEnter={() => setIsOptimizedModelButtonHovered(true)}
                            onMouseLeave={() => setIsOptimizedModelButtonHovered(false)}
                        >
                            <Tooltip
                                interactive
                                titleTranslationKey="STATIC.PAGES.SKADNETWORK.OPTIMIZED_MODEL_AVAILABLE_TOOLTIP"
                                titleTranslationData={{ optimizedModelImprovementPercentage }}
                            >
                                <div
                                    className={classNames(css.optimizedModelButtonLabel, {
                                        [css.optimizedModelButtonLabelHover]: isOptimizedModelButtonHovered,
                                    })}
                                >
                                    {isOptimizedModelButtonHovered ? <SkanPlusWhiteIcon /> : <SkanPlusIcon />}
                                    <span className={css.optimizedModelButtonLabelSpan}>
                                        <Translate id="STATIC.PAGES.SKADNETWORK.OPTIMIZED_MODEL_AVAILABLE" />
                                    </span>
                                </div>
                            </Tooltip>
                        </div>
                    </PermissionsWrapper>
                )}
                <div className={css.modelHeaderFieldContainer}>
                    <div className={css.modelHeaderField}>
                        <Translate id="STATIC.PAGES.SKADNETWORK.MODEL_TYPE" />
                        <div className={css.modelHeaderFieldValue}>{typeDisplayName}</div>
                    </div>
                </div>
                <Toggle
                    className={css.modelToggle}
                    checked={enabled}
                    onToggle={() => onToggleClick(groupId)}
                    disabled={readOnly}
                />
                <div className={css.separator} />
                <div className={css.actionMenuContainer}>
                    <Tooltip
                        titleTranslationKey="STATIC.PAGES.SKADNETWORK.SKAN_4_BETA_TOOLTIP"
                        disabled={isMenuEnabled}
                    >
                        <ContextMenu
                            className={css.actionMenuButton}
                            items={getMenuItems()}
                            onSelect={actionMenuOnSelect}
                            menuDisabled={!isMenuEnabled}
                        />
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};

ConversionModelsGroupHeader.propTypes = {
    groupId: PropTypes.number.isRequired,
    model: conversionModelTypeShape.isRequired,
    name: PropTypes.string.isRequired,
    editable: PropTypes.bool,
    onToggleClick: PropTypes.func,
    onArchiveClick: PropTypes.func,
    relevantOptimizedModels: PropTypes.arrayOf(optimizedModelsShape),
    isSkan4: PropTypes.bool,
    isReadOnly: PropTypes.bool,
    translate: PropTypes.func,
};

export default withLocalize(ConversionModelsGroupHeader);
